import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
	items: [],
	loading: false,
	error: null,
	total: 0,
	lowStockCount: 0,
};

const FETCH_ITEMS_REQUEST = "FETCH_DISPENSABLE_ITEMS_REQUEST";
const FETCH_ITEMS_SUCCESS = "FETCH_DISPENSABLE_ITEMS_SUCCESS";
const FETCH_ITEMS_FAILURE = "FETCH_DISPENSABLE_ITEMS_FAILURE";
const CREATE_ITEM_REQUEST = "CREATE_DISPENSABLE_ITEM_REQUEST";
const CREATE_ITEM_SUCCESS = "CREATE_DISPENSABLE_ITEM_SUCCESS";
const CREATE_ITEM_FAILURE = "CREATE_DISPENSABLE_ITEM_FAILURE";
const UPDATE_ITEM_REQUEST = "UPDATE_DISPENSABLE_ITEM_REQUEST";
const UPDATE_ITEM_SUCCESS = "UPDATE_DISPENSABLE_ITEM_SUCCESS";
const UPDATE_ITEM_FAILURE = "UPDATE_DISPENSABLE_ITEM_FAILURE";
const DELETE_ITEM_REQUEST = "DELETE_DISPENSABLE_ITEM_REQUEST";
const DELETE_ITEM_SUCCESS = "DELETE_DISPENSABLE_ITEM_SUCCESS";
const DELETE_ITEM_FAILURE = "DELETE_DISPENSABLE_ITEM_FAILURE";
const CREATE_ISSUANCE_REQUEST = "CREATE_DISPENSABLE_ISSUANCE_REQUEST";
const CREATE_ISSUANCE_SUCCESS = "CREATE_DISPENSABLE_ISSUANCE_SUCCESS";
const CREATE_ISSUANCE_FAILURE = "CREATE_DISPENSABLE_ISSUANCE_FAILURE";
const RESTOCK_ITEM_REQUEST = "RESTOCK_ITEM_REQUEST";
const RESTOCK_ITEM_SUCCESS = "RESTOCK_ITEM_SUCCESS";
const RESTOCK_ITEM_FAILURE = "RESTOCK_ITEM_FAILURE";

// RESTOCK ITEM

export const restockItem =
	(itemId, restockData, userAgency, userId) => async (dispatch) => {
		const itemService = getService(serviceNames.perDispensableItems);
		const issuanceService = getService(
			serviceNames.perDispensableIssuances,
		);

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: RESTOCK_ITEM_REQUEST,
				success: RESTOCK_ITEM_SUCCESS,
				failure: RESTOCK_ITEM_FAILURE,
			},
			apiCall: async () => {
				const quantity = restockData.quantity || 0;

				// 1. Create restock issuance record
				const issuance = await issuanceService.create({
					itemId: itemId,
					date: new Date().toISOString().slice(0, 10),
					perPersonnelId: null,
					vehicleId: null,
					quantity: -quantity,
					serialId: null,
					issuedBy: userId,
					comments: `Restock +${quantity}${restockData.notes ? ` — ${restockData.notes}` : ""}`,
					totalCost: null,
					isRestock: true,
					purchaseCost: restockData.purchaseCost || null,
					vendor: restockData.vendor || null,
					fundingSourceId: restockData.fundingSourceId || null,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// 2. Get current item and update stock
				const item = await itemService.get(itemId);
				const currentStock = item.unitsInStock || 0;
				const newStock = currentStock + quantity;

				const updateData = {
					unitsInStock: newStock,
					updatedBy: userId,
				};

				// Update item fields if changed
				if (restockData.updateItem) {
					if (restockData.updateItem.vendor !== undefined) {
						updateData.vendor = restockData.updateItem.vendor;
					}
					if (restockData.updateItem.cost !== undefined) {
						updateData.cost = restockData.updateItem.cost;
					}
					if (restockData.updateItem.fundingSourceId !== undefined) {
						updateData.fundingSourceId =
							restockData.updateItem.fundingSourceId;
					}
				}

				const updatedItem = await itemService.patch(itemId, updateData);

				// Fetch updated issuances for the item
				const issuances = await fetchItemIssuances(itemId);

				return {
					item: {
						...updatedItem,
						issuances,
					},
					issuance: issuance,
				};
			},
			successMessage: "Item restocked successfully",
			errorMessagePrefix: "Failed to restock item",
		});
	};

// HELPER FUNCTIONS

// Helper to fetch issuances for items
const fetchItemIssuances = async (itemId) => {
	try {
		const service = getService(serviceNames.perDispensableIssuances);
		const response = await service.find({
			query: {
				itemId: itemId,
				isDeleted: false,
				$limit: -1,
				$sort: { date: -1 },
			},
		});

		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching issuances:", error);
		return [];
	}
};

// FETCH DISPENSABLE ITEMS

export const getDispensableItems =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "desc",
		search = "",
		filters = {},
		userAgency,
		itemType = "Items",
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perDispensableItems);
		const issuancesService = getService(
			serviceNames.perDispensableIssuances,
		);

		// ISSUANCES VIEW

		if (itemType === "Issuances") {
			const query = {
				$limit: pageSize,
				$skip: page * pageSize,
				$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
				isDeleted: false,
				agencyId: userAgency,
			};

			if (search) {
				query.$or = [
					{ serialId: { $like: `%${search}%` } },
					{ comments: { $like: `%${search}%` } },
				];
			}

			return await asyncThunkWrapper({
				dispatch,
				types: {
					request: FETCH_ITEMS_REQUEST,
					success: FETCH_ITEMS_SUCCESS,
					failure: FETCH_ITEMS_FAILURE,
				},
				apiCall: async () => {
					const response = await issuancesService.find({ query });
					const issuancesData = response?.data || response || [];

					// Fetch item details for each issuance
					const itemsService = getService(
						serviceNames.perDispensableItems,
					);
					const issuancesWithDetails = await Promise.all(
						issuancesData.map(async (iss) => {
							try {
								const item = await itemsService.get(iss.itemId);
								return {
									...iss,
									trackingNumber: item?.trackingNumber,
									description: item?.description,
								};
							} catch (error) {
								return {
									...iss,
									trackingNumber: "—",
									description: "—",
								};
							}
						}),
					);

					// Also fetch low stock count for the badge
					const lowStockResponse = await service.find({
						query: {
							agencyId: userAgency,
							isDeleted: false,
							lowStock: true,
							$limit: 0, // Just get count
						},
					});

					return {
						items: issuancesWithDetails,
						total: response.total || issuancesWithDetails.length,
						lowStockCount: lowStockResponse?.total || 0,
					};
				},
				transformResult: (res) => ({
					items: res?.items,
					total: res?.total,
					lowStockCount: res?.lowStockCount,
				}),
				errorMessagePrefix: "Failed to fetch issuances",
			});
		}

		// ITEMS & RESTOCK VIEW

		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		// Text search
		if (search) {
			query.$or = [
				{ trackingNumber: { $like: `%${search}%` } },
				{ description: { $like: `%${search}%` } },
			];
		}

		// Vendor filter
		if (filters.vendor && Array.isArray(filters.vendor)) {
			const values = filters.vendor.map((v) => v.value).filter(Boolean);
			if (values.length > 0) {
				query.vendor = { $in: values };
			}
		}

		// LOW STOCK FILTER - Handled on backend with proper pagination
		if (itemType === "Restock") {
			query.lowStock = true;
		}

		// Always include low stock count for the badge
		query.includeLowStockCount = true;

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_ITEMS_REQUEST,
				success: FETCH_ITEMS_SUCCESS,
				failure: FETCH_ITEMS_FAILURE,
			},
			apiCall: async () => {
				const response = await service.find({ query });
				const itemsData = response?.data || response || [];

				// Fetch issuances for each item
				const itemsWithIssuances = await Promise.all(
					itemsData.map(async (item) => {
						const issuances = await fetchItemIssuances(item.id);
						return {
							...item,
							issuances,
						};
					}),
				);

				return {
					items: itemsWithIssuances,
					total: response.total || itemsWithIssuances.length,
					lowStockCount: response.lowStockCount || 0,
				};
			},
			transformResult: (res) => ({
				items: res?.items,
				total: res?.total,
				lowStockCount: res?.lowStockCount,
			}),
			errorMessagePrefix: "Failed to fetch dispensable items",
		});
	};

// CREATE DISPENSABLE ITEM

export const createDispensableItem =
	(itemData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perDispensableItems);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_ITEM_REQUEST,
				success: CREATE_ITEM_SUCCESS,
				failure: CREATE_ITEM_FAILURE,
			},
			apiCall: async () => {
				const item = await service.create({
					...itemData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});
				return item;
			},
			successMessage: "Dispensable item created successfully",
			errorMessagePrefix: "Failed to create item",
		});
	};

// UPDATE DISPENSABLE ITEM

export const updateDispensableItem =
	(id, itemData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perDispensableItems);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_ITEM_REQUEST,
				success: UPDATE_ITEM_SUCCESS,
				failure: UPDATE_ITEM_FAILURE,
			},
			apiCall: async () => {
				const item = await service.patch(id, {
					...itemData,
					updatedBy: userId,
				});

				// Fetch updated issuances
				const issuances = await fetchItemIssuances(id);

				return {
					...item,
					issuances,
				};
			},
			successMessage: "Item updated successfully",
			errorMessagePrefix: "Failed to update item",
		});
	};

// DELETE DISPENSABLE ITEM

export const deleteDispensableItem = (id, userId) => async (dispatch) => {
	const service = getService(serviceNames.perDispensableItems);
	const issuancesService = getService(serviceNames.perDispensableIssuances);

	const patchData = {
		isDeleted: true,
		deletedBy: userId,
		deletedDateTime: new Date().toISOString(),
	};

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: DELETE_ITEM_REQUEST,
			success: DELETE_ITEM_SUCCESS,
			failure: DELETE_ITEM_FAILURE,
		},
		apiCall: async () => {
			// Soft delete item
			await service.patch(id, patchData);

			// Soft delete all issuances
			const issuances = await issuancesService.find({
				query: { itemId: id, isDeleted: false, $limit: -1 },
			});

			const existingIssuances = issuances?.data || issuances || [];
			for (const iss of existingIssuances) {
				await issuancesService.patch(iss.id, {
					isDeleted: true,
					deletedBy: userId,
					deletedDateTime: new Date().toISOString(),
				});
			}

			return id;
		},
		transformResult: () => id,
		successMessage: "Item deleted successfully",
		errorMessagePrefix: "Failed to delete item",
	});
};

// CREATE ISSUANCE (Issue Item)

export const createIssuance =
	(itemId, issuanceData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perDispensableIssuances);
		const itemsService = getService(serviceNames.perDispensableItems);

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_ISSUANCE_REQUEST,
				success: CREATE_ISSUANCE_SUCCESS,
				failure: CREATE_ISSUANCE_FAILURE,
			},
			apiCall: async () => {
				// Get current item to calculate total cost and update stock
				const item = await itemsService.get(itemId);

				const quantity = issuanceData.quantity || 0;
				const unitCost = item.cost != null ? Number(item.cost) : 0;
				const totalCost = quantity * unitCost;

				// Create issuance
				const issuance = await service.create({
					itemId: itemId, // Fixed: Use itemId to match model
					date:
						issuanceData.date ||
						new Date().toISOString().slice(0, 10),
					perPersonnelId: issuanceData.employeeID || null,
					vehicleId: issuanceData.vehicleID || null,
					quantity: quantity,
					serialId: issuanceData.serialId || null,
					issuedBy: userId,
					comments: issuanceData.comments || null,
					totalCost: totalCost,
					isRestock: false,
					purchaseCost: null,
					vendor: null,
					fundingSourceId: null,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// Update item stock
				const newStock = Math.max(
					0,
					(item.unitsInStock || 0) - quantity,
				);
				const updatedItem = await itemsService.patch(itemId, {
					unitsInStock: newStock,
					updatedBy: userId,
				});

				// Fetch updated issuances for the item
				const issuances = await fetchItemIssuances(itemId);

				return {
					item: {
						...updatedItem,
						issuances,
					},
					issuance: issuance,
				};
			},
			successMessage: "Item issued successfully",
			errorMessagePrefix: "Failed to issue item",
		});
	};

// REDUCER

export default function dispensableItemsReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_ITEMS_REQUEST:
		case CREATE_ITEM_REQUEST:
		case UPDATE_ITEM_REQUEST:
		case DELETE_ITEM_REQUEST:
		case CREATE_ISSUANCE_REQUEST:
		case RESTOCK_ITEM_REQUEST:
			return { ...state, loading: true, error: null };

		case FETCH_ITEMS_SUCCESS:
			return {
				...state,
				loading: false,
				items: action.payload.items,
				total: action.payload.total,
				lowStockCount:
					action.payload.lowStockCount ?? state.lowStockCount,
			};

		case CREATE_ITEM_SUCCESS:
			return {
				...state,
				loading: false,
				items: [action.payload, ...state.items],
				total: state.total + 1,
			};

		case UPDATE_ITEM_SUCCESS:
			return {
				...state,
				loading: false,
				items: state.items.map((i) =>
					i.id === action.payload.id ? action.payload : i,
				),
			};

		case DELETE_ITEM_SUCCESS:
			return {
				...state,
				loading: false,
				items: state.items.filter((i) => i.id !== action.payload),
				total: state.total - 1,
			};

		case CREATE_ISSUANCE_SUCCESS:
			// Update the item in the list with new stock and issuances
			return {
				...state,
				loading: false,
				items: state.items.map((item) =>
					item.id === action.payload.item.id
						? action.payload.item
						: item,
				),
			};

		case RESTOCK_ITEM_SUCCESS:
			// Update the item and recalculate low stock count
			const updatedItems = state.items.map((item) =>
				item.id === action.payload.item.id ? action.payload.item : item,
			);

			// Recalculate low stock count
			const newLowStockCount = updatedItems.filter((item) => {
				const stock = item.unitsInStock || 0;
				const min = item.minBeforeRestock || 0;
				return stock <= min;
			}).length;

			return {
				...state,
				loading: false,
				items: updatedItems,
				lowStockCount: newLowStockCount,
			};

		case FETCH_ITEMS_FAILURE:
		case CREATE_ITEM_FAILURE:
		case UPDATE_ITEM_FAILURE:
		case DELETE_ITEM_FAILURE:
		case CREATE_ISSUANCE_FAILURE:
		case RESTOCK_ITEM_FAILURE:
			return { ...state, loading: false, error: action.payload };

		default:
			return state;
	}
}
